import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authApi } from '@/api/client';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { BrandMark, SlashField } from '@/components/BrandMark';

const MIN_LENGTH = 10;

function readTokenFromUrl(): string {
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  return (search.get('token') ?? hash.get('token') ?? '').trim();
}

export function ResetPassword() {
  const navigate = useNavigate();
  const [token] = useState(readTokenFromUrl);
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [linkInvalid, setLinkInvalid] = useState(!token);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  // Drop the token from the address bar once read so it isn't kept in browser history.
  useEffect(() => {
    if (!token) return;
    try {
      window.history.replaceState(window.history.state, '', window.location.pathname);
    } catch {
      /* ignore */
    }
  }, [token]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (next.length < MIN_LENGTH) {
      setError(`New password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (next !== confirm) {
      setError('The new passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      await authApi.resetPassword(token, next);
      setDone(true);
    } catch (err) {
      const message = (err as { message?: string }).message ?? 'Could not reset your password.';
      const status = (err as { status?: number }).status;
      if (status === 429) {
        setError('Too many attempts from this device. Please wait a while and try again.');
      } else if (/expired|already used/i.test(message)) {
        setLinkInvalid(true);
      } else {
        setError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  const goToSignIn = () =>
    navigate('/login', {
      replace: true,
      state: { notice: 'Password updated. Sign in with your new password.' },
    });

  let card: React.ReactNode;
  if (done) {
    card = (
      <div className="auth-form" role="status">
        <p className="kicker" style={{ color: 'var(--color-ink)' }}>All set</p>
        <h2>Password updated</h2>
        <p className="auth-help">You can now sign in with your new password.</p>
        <Button type="button" variant="signal" size="lg" fullWidth onClick={goToSignIn}>
          Sign in
        </Button>
      </div>
    );
  } else if (linkInvalid) {
    card = (
      <div className="auth-form">
        <p className="kicker" style={{ color: 'var(--color-ink)' }}>Reset password</p>
        <h2>This link can't be used</h2>
        <p className="auth-help">
          {token
            ? 'This reset link has expired or was already used. Request a new one.'
            : 'This reset link is missing or incomplete. Open the link from your email again, or request a new one.'}
        </p>
        <p className="auth-help">
          On the sign-in page, choose "Forgot password?" to get a new link. Links expire after 1 hour.
        </p>
        <Link to="/login" className="auth-link">Back to sign in</Link>
      </div>
    );
  } else {
    card = (
      <form className="auth-form" onSubmit={handleSubmit}>
        <p className="kicker" style={{ color: 'var(--color-ink)' }}>Reset password</p>
        <h2>Choose a new password</h2>

        {error && <div className="auth-error" role="alert">{error}</div>}

        <Input
          label="New password"
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          required
          minLength={MIN_LENGTH}
          autoComplete="new-password"
          hint={`At least ${MIN_LENGTH} characters`}
        />
        <Input
          label="Confirm new password"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
          autoComplete="new-password"
        />

        <Button type="submit" variant="signal" size="lg" fullWidth loading={saving}>
          Update password
        </Button>
        <p className="auth-alt">
          <Link to="/login" className="auth-link">Back to sign in</Link>
        </p>
      </form>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-hero">
        <BrandMark variant="light" size="lg" product="DispatchDash" />
      </div>

      {card}

      <div className="auth-footer">
        <SlashField />
        <span>11elevenlogistics.ph</span>
      </div>

      <style>{`
        .auth-page {
          min-height: 100dvh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: var(--space-xl);
          background: var(--color-ink);
        }
        .auth-hero {
          width: 100%;
          max-width: 400px;
          margin-bottom: var(--space-xl);
        }
        .auth-form {
          background: var(--color-white);
          padding: var(--space-xl);
          border-radius: var(--radius-md);
          width: 100%;
          max-width: 400px;
          box-shadow: var(--shadow-md);
        }
        .auth-form h2 {
          margin-bottom: var(--space-lg);
          color: var(--color-ink);
        }
        .auth-help {
          color: var(--color-steel);
          font-size: 0.875rem;
          margin: calc(-1 * var(--space-sm)) 0 var(--space-lg);
        }
        .auth-error {
          background: #FDE8E8;
          color: var(--color-urgent);
          padding: var(--space-sm) var(--space-md);
          border-radius: var(--radius-sm);
          margin-bottom: var(--space-md);
          font-size: 0.875rem;
        }
        .auth-alt {
          text-align: center;
          margin-top: var(--space-md);
          font-size: 0.875rem;
        }
        .auth-link {
          background: none;
          border: none;
          padding: 0;
          color: var(--color-ink);
          text-decoration: underline;
          cursor: pointer;
          font: inherit;
          font-size: 0.875rem;
        }
        .auth-footer {
          width: 100%;
          max-width: 400px;
          margin-top: var(--space-2xl);
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          color: var(--color-steel);
          font-size: 0.75rem;
          letter-spacing: 0.04em;
        }
      `}</style>
    </div>
  );
}
